/**
 * Preset catalog for the editor-v2 Photo Edit runner.
 *
 * These are NOT registry tools and NOT the Headshots/Avatars/Change Clothes studios.
 * Each row is one targeted JTBD: prompt + optional UI slots + optional preview.
 * Launch is always `/photo-editor/{id}` (editor-v2 shell).
 *
 * `mapsToToolId` is a related studio (for later "open the full tool" links),
 * not the editor this preset opens.
 */
import catalogJson from "./ai-photo-editor.json";

export const PHOTO_EDITOR_REGISTRY_TOOL_ID = "ai-photo-editor";
export const PHOTO_EDITOR_CDN =
  process.env.NEXT_PUBLIC_PHOTO_EDITOR_CDN_URL ||
  "https://pfpmakerai-v3.sfo3.cdn.digitaloceanspaces.com";
const PHOTO_EDITOR_LOCAL_PREFIX = "/assets/images/ai-photo-editor/";
export const GO_VIRAL_CATEGORY_ID = "go-viral";
export const GO_VIRAL_AVATAR_STYLE_COUNT = 40;
export const GO_VIRAL_AVATARS_HREF = "/editor-v2/ai-avatars";

/** Whole dashboard sections hidden until they have shippable cards (catalog rows stay). */
export const DASHBOARD_HIDDEN_CATEGORY_IDS = new Set<string>();

/** Preset ids hidden from dashboard cards. */
export const DASHBOARD_HIDDEN_TOOL_IDS = new Set<string>();

export function isDashboardVisibleCategory(category: PhotoEditorCategory): boolean {
  return !DASHBOARD_HIDDEN_CATEGORY_IDS.has(category.id);
}

export function isDashboardVisibleTool(tool: PhotoEditorTool): boolean {
  if (DASHBOARD_HIDDEN_TOOL_IDS.has(tool.id)) return false;
  if (tool.categoryId === GO_VIRAL_CATEGORY_ID) return false;
  if (tool.status === "planned") return false;
  return true;
}
export const PRESET_DEFAULT_CREDIT_COST = 2;

/**
 * How a dashboard section is filled:
 * - rail     — curated view (Popular / New / Editor's picks). Tools are picked
 *              by a rank flag (`select`), not by categoryId.
 * - looks    — same horizontal row as a rail, but the items live here:
 *              film/camera looks whose categoryId is this row (e.g. Polaroid).
 * - category — topical JTBD group (Retouch, Beauty, Style…). A tool's home.
 */
export type PhotoEditorCategoryKind = "rail" | "looks" | "category";
export type PhotoEditorRailSelect = "popular" | "isNew" | "editorsPick";
export type PhotoEditorStatus = "live" | "partial" | "planned";
export type PhotoEditorProductFit =
  | "portrait-editor"
  | "fashion-studio"
  | "avatars"
  | "brand"
  | "pets";
export type PhotoEditorInput = "portrait" | "any-photo" | "pet" | "product";

export interface PhotoEditorCategory {
  id: string;
  title: string;
  emoji?: string;
  kind: PhotoEditorCategoryKind;
  sort: number;
  tagline?: string;
  description?: string;
  /** Rail only: which rank field fills this view (`popular` / `isNew` / `editorsPick`). */
  select?: PhotoEditorRailSelect;
  cta?: {
    label: string;
    toolId: string;
  };
}

/** Extra reference upload (garment, product, hair ref). Separate from the subject selfie. */
export interface PhotoEditorRefSlot {
  id: string;
  label: string;
  hint?: string;
  max?: number;
}

export type PhotoEditorPreview =
  | { type: "fade-cycle"; images: string[] }
  | { type: "before-after"; pairs: Array<{ before: string; after: string }> }
  | { type: "static"; image: string };

export interface PhotoEditorSettings {
  aspectRatio?: string;
  modelId?: string;
  resolution?: "1K" | "2K" | "4K";
}

/** Preset pose option (Pose Editor and similar). */
export interface PhotoEditorPoseOption {
  id: string;
  label: string;
  image: string;
  prompt: string;
}

export interface PhotoEditorTool {
  id: string;
  title: string;
  tagline: string;
  categoryId: string;
  /** 1-based rank in the Popular rail. Omit if not popular. */
  popular?: number;
  /** 1-based rank in the New rail. Omit if not new. */
  isNew?: number;
  /** 1-based rank in Editor's picks. Omit if not a pick. */
  editorsPick?: number;
  sort?: number;
  status: PhotoEditorStatus;
  /** Related studio tool id (Change Clothes, etc). Not the launch target. */
  mapsToToolId?: string;
  productFit: PhotoEditorProductFit;
  input?: PhotoEditorInput;
  searchTerms?: string[];
  job?: string;
  prompt: string;
  /** Overrides catalog identityLock when set. Set false to disable identity lock (e.g. face swap). */
  identityLock?: string | false;
  /**
   * In-place photo lock (outfit / scene / crop), appended last so option prompts cannot
   * override with demo-card wardrobe. Catalog default applies unless set false on full restyles
   * (outfit swap, go-viral transforms, cartoonizer).
   */
  photoLock?: string | false;
  /** Flat credit cost. Defaults to PRESET_DEFAULT_CREDIT_COST. */
  creditCost?: number;
  poses?: PhotoEditorPoseOption[];
  refs?: PhotoEditorRefSlot[];
  preview?: PhotoEditorPreview;
  settings?: PhotoEditorSettings;
}

export interface PhotoEditorCatalog {
  id: string;
  title: string;
  tagline: string;
  href: string;
  version: number;
  identityLock: string;
  photoLock: string;
  categories: PhotoEditorCategory[];
  tools: PhotoEditorTool[];
}

export const photoEditorCatalog = rewriteCatalogAssets(catalogJson as PhotoEditorCatalog);

/** Dashboard/editor card URLs on the Spaces CDN. Catalog JSON keeps local paths. */
export function photoEditorAssetUrl(src: string): string {
  if (!src || src.startsWith("http://") || src.startsWith("https://")) return src;
  if (!src.startsWith(PHOTO_EDITOR_LOCAL_PREFIX)) return src;
  return `${PHOTO_EDITOR_CDN}/ai-photo-editor/${src.slice(PHOTO_EDITOR_LOCAL_PREFIX.length)}`;
}

function rewriteCatalogAssets(catalog: PhotoEditorCatalog): PhotoEditorCatalog {
  return {
    ...catalog,
    tools: catalog.tools.map((tool) => ({
      ...tool,
      preview: rewritePreview(tool.preview),
      poses: tool.poses?.map((pose) => ({ ...pose, image: photoEditorAssetUrl(pose.image) })),
    })),
  };
}

function rewritePreview(preview: PhotoEditorPreview | undefined): PhotoEditorPreview | undefined {
  if (!preview) return preview;
  if (preview.type === "fade-cycle") {
    return { ...preview, images: preview.images.map(photoEditorAssetUrl) };
  }
  if (preview.type === "before-after") {
    return {
      ...preview,
      pairs: preview.pairs.map((pair) => ({
        before: photoEditorAssetUrl(pair.before),
        after: photoEditorAssetUrl(pair.after),
      })),
    };
  }
  return { ...preview, image: photoEditorAssetUrl(preview.image) };
}

function rankOf(tool: PhotoEditorTool, field: PhotoEditorRailSelect): number {
  return tool[field] ?? 0;
}

export function getPhotoEditorCategory(categoryId: string): PhotoEditorCategory | undefined {
  return photoEditorCatalog.categories.find((c) => c.id === categoryId);
}

/** Categories in dashboard filter order (rails first). */
export function getPhotoEditorFilterCategories(): PhotoEditorCategory[] {
  return [...photoEditorCatalog.categories]
    .filter(isDashboardVisibleCategory)
    .sort((a, b) => a.sort - b.sort);
}

/** JTBD groups only — no Popular/New rails. */
export function getPhotoEditorJtbdCategories(): PhotoEditorCategory[] {
  return getPhotoEditorFilterCategories().filter((category) => category.kind === "category");
}

/** First poster frame for a catalog preview (after-image when it's a pair). */
export function getPhotoEditorPreviewPoster(preview?: PhotoEditorPreview): string | undefined {
  if (!preview) return undefined;
  if (preview.type === "fade-cycle") return preview.images[0];
  if (preview.type === "before-after") return preview.pairs[0]?.after;
  return preview.image;
}

/** Tools for a rail, looks row, or category, in display order. */
export function getToolsForCategory(categoryId: string): PhotoEditorTool[] {
  const category = getPhotoEditorCategory(categoryId);
  if (!category) return [];
  if (category.id === GO_VIRAL_CATEGORY_ID) return [];

  if (category.kind === "rail" && category.select) {
    const field = category.select;
    return photoEditorCatalog.tools
      .filter((tool) => typeof tool[field] === "number")
      .filter(isDashboardVisibleTool)
      .sort((a, b) => rankOf(a, field) - rankOf(b, field));
  }

  return photoEditorCatalog.tools
    .filter((tool) => tool.categoryId === categoryId)
    .filter(isDashboardVisibleTool)
    .sort((a, b) => (a.sort ?? 99) - (b.sort ?? 99));
}

export function getPhotoEditorTool(id: string): PhotoEditorTool | undefined {
  return photoEditorCatalog.tools.find((tool) => tool.id === id);
}

export function buildPhotoEditorPrompt(tool: PhotoEditorTool, posePrompt?: string): string {
  const lock =
    tool.identityLock === false ? null : tool.identityLock ?? photoEditorCatalog.identityLock;
  const photoLock =
    tool.photoLock === false ? null : tool.photoLock ?? photoEditorCatalog.photoLock;
  const parts = [lock, tool.prompt, posePrompt];
  if (tool.id === "face-swap") {
    parts.push(
      "Image 1 is the photo to recreate 1:1. Image 2 is identity reference only — never paste or resize Image 2's face onto Image 1. Rebuild the person in Image 1's exact expression, head size, angle, and proportions."
    );
  }
  if (tool.id === "gender-swap") {
    parts.push(
      "The subject's eyes must look exactly at the same angle as the original photo. This is identity-preserving gender transformation — not a different person."
    );
  }
  parts.push(photoLock);
  return parts.filter(Boolean).join(" ").trim();
}

export function getPresetCreditCost(tool: PhotoEditorTool): number {
  return typeof tool.creditCost === "number" && tool.creditCost > 0
    ? tool.creditCost
    : PRESET_DEFAULT_CREDIT_COST;
}

export function buildPresetHref(toolId: string): string {
  return `/photo-editor/${encodeURIComponent(toolId)}`;
}

export function getDefaultPresetTool(): PhotoEditorTool {
  return getToolsForCategory("popular")[0] ?? photoEditorCatalog.tools[0];
}
